import { useCallback, useMemo, useState } from "react";
import { MarketChart } from "../../shared/charts/market-chart.js";
import type { ChartBarView } from "../../shared/charts/chart-models.js";
import { analysisChartModel } from "./analysis-chart-model.js";
import type { AnalysisReport } from "./queries.js";

const price = (value: number) => `$${value.toFixed(2)}`;
const annotationColor = { bullish: "#6ee7ad", bearish: "#ff8c80", neutral: "#ffcf66" };

export function AnalysisChart({ report }: { readonly report: AnalysisReport }) {
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [selectedAnnotation, setSelectedAnnotation] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const model = useMemo(() => analysisChartModel(report), [report]);
  const bars = useMemo(
    () =>
      model.candles.map((candle): ChartBarView => ({
        ...candle,
        sessionDate: candle.date,
        // Daily mode uses the saved session date, not an assumed market-hours timestamp.
        startedAt: `${candle.date}T00:00:00Z`,
        endedAt: `${candle.date}T00:00:00Z`,
        sessionPhase: "regular",
      })),
    [model],
  );
  const chartAnnotations = useMemo(
    () =>
      showAnnotations
        ? model.annotations.map((item, index) => ({
            ...item,
            id: String(index),
            color: annotationColor[item.tone],
          }))
        : [],
    [model, showAnnotations],
  );
  const evidenceSessionDates = useMemo(
    () =>
      model.candles
        .filter((candle) => report.decision?.evidenceIds.includes(candle.evidenceId))
        .map((candle) => candle.date),
    [model, report],
  );
  const selectBar = useCallback((bar: ChartBarView) => setSelectedDate(bar.sessionDate), []);
  const { candles, annotations } = model;
  if (model.error !== undefined)
    return <p className="field-hint">Analysis chart unavailable: {model.error}</p>;
  const last = candles.at(-1);
  if (last === undefined) return null;
  const selectedCandle = candles.find((candle) => candle.date === selectedDate) ?? last;
  const annotation =
    showAnnotations && selectedAnnotation !== undefined
      ? annotations[Number(selectedAnnotation)]
      : undefined;
  return (
    <section className="analysis-chart section-stack" aria-label="Annotated analysis chart">
      <div className="connection-heading">
        <div>
          <h3>{report.symbol} daily price and volume</h3>
          <p className="field-hint">
            Saved split-adjusted evidence · {candles[0]?.date} – {last.date} · {candles.length}{" "}
            sessions
          </p>
        </div>
        <label className="check-field">
          <input
            type="checkbox"
            checked={showAnnotations}
            onChange={(event) => setShowAnnotations(event.currentTarget.checked)}
          />
          <span>Show annotations</span>
        </label>
      </div>
      <p className="field-hint">
        Zoom, pan or move the crosshair to inspect candles. Select a candle or numbered annotation
        to read its details. Yellow evidence markers identify candles cited in the report.
      </p>
      <MarketChart
        bars={bars}
        timeframe="daily"
        annotations={chartAnnotations}
        evidenceSessionDates={evidenceSessionDates}
        onAnnotationSelect={setSelectedAnnotation}
        onBarSelect={selectBar}
        {...(selectedAnnotation === undefined ? {} : { selectedAnnotationId: selectedAnnotation })}
      />
      <label className="field-stack">
        <span>Saved candle</span>
        <select
          value={selectedCandle.date}
          onChange={(event) => setSelectedDate(event.currentTarget.value)}
        >
          {candles.map((candle) => (
            <option key={candle.date} value={candle.date}>
              {candle.date}
            </option>
          ))}
        </select>
      </label>
      <p className="analysis-chart-readout" aria-live="polite">
        {selectedCandle.date} · Open {price(selectedCandle.open)} · High{" "}
        {price(selectedCandle.high)} · Low {price(selectedCandle.low)} · Close{" "}
        {price(selectedCandle.close)} · Volume {selectedCandle.volume.toLocaleString()}
      </p>
      <p className="field-hint">
        Last saved close {price(last.close)}. This chart stays frozen with this report; it does not
        use live prices.
      </p>
      {model.annotationError === undefined ? null : (
        <p role="status" className="field-hint">
          {model.annotationError}
        </p>
      )}
      {annotations.length === 0 ? (
        <p className="field-hint">No AI annotations were saved for this report.</p>
      ) : !showAnnotations ? null : (
        <>
          <div className="analysis-chart-legend">
            {annotations.map((item, index) => (
              <button
                type="button"
                className="button button--quiet"
                key={index}
                aria-pressed={selectedAnnotation === String(index)}
                onClick={() => setSelectedAnnotation(String(index))}
              >
                <span
                  className="analysis-chart-badge"
                  style={{ background: annotationColor[item.tone] }}
                >
                  {index + 1}
                </span>
                <span>
                  {item.label} · {price(item.lowerPrice)}
                  {item.upperPrice === item.lowerPrice ? "" : ` – ${price(item.upperPrice)}`}
                </span>
              </button>
            ))}
          </div>
          {annotation === undefined ? (
            <p className="field-hint">Select an annotation above to see its explanation.</p>
          ) : (
            <div className="analysis-chart-explanation" aria-live="polite">
              <strong>{annotation.label}</strong>
              <p>{annotation.explanation}</p>
              <small>
                {annotation.startSessionDate} – {annotation.endSessionDate} · Evidence:{" "}
                {annotation.evidenceIds.join(", ")}
              </small>
            </div>
          )}
        </>
      )}
    </section>
  );
}
